import React, { useEffect, useMemo } from 'react';

import Button from '@amzn/meridian/button';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import useForm from 'src/hooks/useForm';
import JmrService from 'src/services/jmr/jmrService';
import { type NonPhysicalLimitation } from 'src/services/jmr/types';
import {
  type DownloadTWPAForm,
  type PlacementDetailsAllocatedJobShift,
  TemporaryWorkPlacementEnum,
} from 'src/store/placementDetailsPageSlice/types';
import { useAppSelector } from 'src/store/store';
import { createFileUrl, downloadFile } from 'src/utils/fileUtils';

import GeneralInfo from './generalInfo/generalInfo';
import { TWPRadioOptions } from './twpSelector/constants';
import TWPSelector from './twpSelector/twpSelector';

// ================================= Types ================================= //

interface TwpaModalProps {
  placementId: number;
  isTwpaModalOpen: boolean;
  closeTwpaModal: () => void;
  allocatedJobShifts: PlacementDetailsAllocatedJobShift[];
  nonPhysicalLimitations: NonPhysicalLimitation[];
  isJobModificationsEnabledForSite: boolean;
  isNonPhysicalLimitationsEnabledForSite: boolean;
}

// =============================== Components ============================== //

/**
 * TWPA Modal
 */
const TwpaModal = ({
  placementId,
  isTwpaModalOpen,
  closeTwpaModal,
  isJobModificationsEnabledForSite,
  isNonPhysicalLimitationsEnabledForSite,
  allocatedJobShifts,
  nonPhysicalLimitations,
}: TwpaModalProps) => {
  const { featureFlags } = useAppSelector((state) => state.user);
  const jobModificationsEnabled = featureFlags.JOB_MODIFICATIONS && !!isJobModificationsEnabledForSite;
  const includesT2PlusModified =
    allocatedJobShifts?.some((ajs) => ajs.isTierTwoPlusJobModified) && featureFlags.T2_PLUS_EXPERIENCE;
  const nonPhysicalLimitationsEnabled = featureFlags.NON_PHYSICAL_LIMITATIONS && isNonPhysicalLimitationsEnabledForSite;

  // Setup Form Hook
  const {
    handleChange,
    handleChangeDynamicKey,
    validateForm,
    resetFormField,
    resetForm,
    errors: twpaFormErrors,
    formData: twpaFormData,
  } = useForm<DownloadTWPAForm>({
    validations: {
      department: {
        required: { value: true, message: 'Department is required' },
      },
      placementShifts: {
        required: { value: true, message: 'Placement shift(s) is required' },
      },
      reportDate: {
        required: { value: true, message: 'Report date is required' },
      },
      workPlacementProvide: {
        required: {
          value: true,
          message: 'Please select one',
        },
      },
    },
  });

  // Dedupe and set job modifications
  useEffect(() => {
    resetForm();
    const dedupedAppliedModifications = jobModificationsEnabled
      ? [
          ...new Set(allocatedJobShifts?.flatMap((ajs) => ajs.appliedModifications.map((am) => am.modificationName))),
        ].join(', ')
      : '';
    handleChange({ jobModifications: dedupedAppliedModifications });
  }, [isTwpaModalOpen, allocatedJobShifts, jobModificationsEnabled]);

  // Reset 'workPlacementProvide'
  useEffect(() => {
    if (
      twpaFormData.jobModifications?.length &&
      twpaFormData.workPlacementProvide === TemporaryWorkPlacementEnum.MATCH_ROLE_NO_MODIFICATIONS
    )
      resetFormField('workPlacementProvide');
  }, [twpaFormData.jobModifications]);

  // Setup the radio button options
  const twpOptions = useMemo(() => {
    const showNoJobModificationsOption =
      !twpaFormData.jobModifications?.length && !allocatedJobShifts?.some((ajs) => !!ajs.modificationNotes);
    const { MATCH_ROLE_WITH_MODIFICATIONS, MATCH_ROLE_NO_MODIFICATIONS, NO_MATCH } = TWPRadioOptions;
    if (showNoJobModificationsOption) {
      return [MATCH_ROLE_WITH_MODIFICATIONS, MATCH_ROLE_NO_MODIFICATIONS, NO_MATCH];
    } else {
      return [MATCH_ROLE_WITH_MODIFICATIONS, NO_MATCH];
    }
  }, [twpaFormData.jobModifications?.length, allocatedJobShifts]);

  // Download TWPA PDF
  const downloadTWPA = async () => {
    const response = await JmrService.downloadTWPA({
      accommodationId: placementId,
      reportDate: twpaFormData.reportDate,
      placementShift: twpaFormData.placementShifts,
      department: twpaFormData.department,
      notes: twpaFormData.notes,
      jobModifications: twpaFormData.jobModifications,
      workPlacementProvide: TemporaryWorkPlacementEnum[twpaFormData.workPlacementProvide],
      isFullDuty: twpaFormData.isFullDuty, // overwritten by backend
    });
    if (response.data) {
      downloadFile(createFileUrl(response.data, 'application/pdf'), 'twpa-form.pdf');
      resetForm();
      closeTwpaModal();
    }
  };

  return (
    <Modal
      title="Temporary Work Placement Acknowledgement Letter"
      open={isTwpaModalOpen}
      onClose={closeTwpaModal}
      width="680px"
    >
      <GeneralInfo
        twpaFormData={twpaFormData}
        twpaFormErrors={twpaFormErrors}
        handleChange={handleChange}
        jobModificationsEnabled={jobModificationsEnabled}
        tierTwoExperienceEnabled={includesT2PlusModified}
        nonPhysicalLimitationsEnabled={nonPhysicalLimitationsEnabled}
        allocatedJobShifts={allocatedJobShifts}
        nonPhysicalLimitations={nonPhysicalLimitations}
      />
      <TWPSelector
        twpaFormData={twpaFormData}
        twpaFormErrors={twpaFormErrors}
        textInfo={
          <>
            Select which <strong>Temporary Work Placement</strong> will be provided.
          </>
        }
        formDataKey="workPlacementProvide"
        radioOptions={twpOptions}
        handleChange={handleChangeDynamicKey}
      />
      <Row widths={['fill', '244px']}>
        <div />
        <Row>
          <Button
            onClick={() => {
              resetForm();
              closeTwpaModal();
            }}
          >
            Cancel
          </Button>
          <Button onClick={() => (validateForm() ? downloadTWPA() : null)}>Generate PDF</Button>
        </Row>
      </Row>
    </Modal>
  );
};

// ================================= Export ================================ //

export default TwpaModal;
